import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BadgeCheck, Loader2, ShieldCheck, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { firmsApi, saasApi } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';

export default function ClaimProfile() {
  const { slug } = useParams();
  const { user, firm: ownFirm, loading, login, refresh } = useAuth();
  const { toast } = useToast();
  const [firm, setFirm] = useState(null);
  const [creds, setCreds] = useState({ email: '', password: '' });
  const [authBusy, setAuthBusy] = useState(false);
  const [claim, setClaim] = useState({ position: '', oab: '', termsAccepted: false });
  const [claimBusy, setClaimBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { firmsApi.get(slug).then(setFirm).catch(() => {}); }, [slug]);

  if (loading || !firm) return <div className="min-h-[60vh] flex items-center justify-center text-[#52676e]">Carregando...</div>;

  const doLogin = async () => {
    setAuthBusy(true);
    try { await login(creds.email, creds.password); }
    catch (err) { toast({ title: 'Erro', description: err.response?.data?.detail || 'Credenciais inválidas.', variant: 'destructive' }); }
    finally { setAuthBusy(false); }
  };

  const doRegister = async () => {
    setAuthBusy(true);
    try {
      const data = await saasApi.registerBasic(creds);
      localStorage.setItem('advbr_token', data.token);
      await refresh();
    } catch (err) { toast({ title: 'Erro', description: err.response?.data?.detail || 'Falha ao criar conta.', variant: 'destructive' }); }
    finally { setAuthBusy(false); }
  };

  const submitClaim = async () => {
    if (!claim.position || !claim.termsAccepted) {
      toast({ title: 'Preencha os campos', description: 'Informe seu vínculo e aceite os termos.', variant: 'destructive' });
      return;
    }
    setClaimBusy(true);
    try {
      await saasApi.claim(firm.id, claim);
      await refresh();
      setDone(true);
    } catch (err) { toast({ title: 'Erro', description: err.response?.data?.detail || 'Falha na reivindicação.', variant: 'destructive' }); }
    finally { setClaimBusy(false); }
  };

  const alreadyClaimed = firm.claimStatus !== 'nao_reivindicado';

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEO title={`Reivindicar perfil: ${firm.name}`} description={`Este perfil é seu? Reivindique gratuitamente o perfil de ${firm.name}.`} />
      <Link to={`/escritorio/${slug}`} className="inline-flex items-center gap-1 text-sm text-[#425a62] hover:text-[#153e44] mb-4"><ArrowLeft className="w-4 h-4" /> Voltar ao perfil</Link>

      <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-6 flex items-center gap-4">
        <img src={firm.photo} alt="" className="w-16 h-16 rounded-[7px] object-cover" />
        <div>
          <h1 className="text-xl font-semibold text-[#153e44]">{firm.name}</h1>
          <p className="text-sm text-[#52676e]">{firm.cityName}/{firm.state} • {firm.primaryCategory}</p>
        </div>
      </div>

      {done ? (
        <div className="mt-6 bg-white border-2 border-emerald-200 rounded-[10px] p-8 text-center" data-testid="claim-success">
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-[#153e44]">Perfil reivindicado com sucesso!</h2>
          <p className="text-[#425a62] mt-2 text-sm">Sua reivindicação foi registrada e será verificada pela equipe. Você já pode editar seus dados e explorar os planos.</p>
          <div className="flex gap-3 justify-center mt-6">
            <Link to="/painel"><Button className="bg-[#153e44] hover:bg-[#0c3036]">Ir ao painel</Button></Link>
            <Link to="/planos"><Button variant="outline">Ver planos</Button></Link>
          </div>
        </div>
      ) : alreadyClaimed ? (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-[10px] p-6 text-amber-800 text-sm" data-testid="claim-already">
          Este perfil já foi reivindicado. Se você é o titular e acredita que houve erro, entre em contato pelo canal de denúncias.
        </div>
      ) : (
        <>
          {!user ? (
            <div className="mt-6 bg-white border border-[#cbd8dc] rounded-[10px] p-6">
              <h2 className="font-semibold text-[#153e44] mb-1">1. Crie sua conta ou entre</h2>
              <p className="text-sm text-[#52676e] mb-4">Necessário para vincular o perfil a você.</p>
              <Tabs defaultValue="criar">
                <TabsList className="w-full"><TabsTrigger value="criar" className="flex-1">Criar conta</TabsTrigger><TabsTrigger value="entrar" className="flex-1">Já tenho conta</TabsTrigger></TabsList>
                <TabsContent value="criar" className="space-y-3 mt-4">
                  <Input data-testid="claim-register-email" type="email" placeholder="E-mail profissional" value={creds.email} onChange={e => setCreds({ ...creds, email: e.target.value })} />
                  <Input data-testid="claim-register-password" type="password" placeholder="Senha (mín. 6 caracteres)" value={creds.password} onChange={e => setCreds({ ...creds, password: e.target.value })} />
                  <Button onClick={doRegister} disabled={authBusy} className="w-full bg-[#153e44] hover:bg-[#0c3036]" data-testid="claim-register-btn">
                    {authBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Criar conta e continuar'}
                  </Button>
                </TabsContent>
                <TabsContent value="entrar" className="space-y-3 mt-4">
                  <Input data-testid="claim-login-email" type="email" placeholder="E-mail" value={creds.email} onChange={e => setCreds({ ...creds, email: e.target.value })} />
                  <Input data-testid="claim-login-password" type="password" placeholder="Senha" value={creds.password} onChange={e => setCreds({ ...creds, password: e.target.value })} />
                  <Button onClick={doLogin} disabled={authBusy} className="w-full bg-[#153e44] hover:bg-[#0c3036]" data-testid="claim-login-btn">
                    {authBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Entrar e continuar'}
                  </Button>
                </TabsContent>
              </Tabs>
            </div>
          ) : ownFirm ? (
            <div className="mt-6 bg-amber-50 border border-amber-200 rounded-[10px] p-6 text-amber-800 text-sm">
              Sua conta já está vinculada ao escritório <strong>{ownFirm.name}</strong>. Cada conta pode gerenciar apenas um perfil.
            </div>
          ) : (
            <div className="mt-6 bg-white border border-[#cbd8dc] rounded-[10px] p-6">
              <h2 className="font-semibold text-[#153e44] mb-1 flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-[#075a6d]" /> 2. Confirme seu vínculo</h2>
              <p className="text-sm text-[#52676e] mb-4">Conectado como <strong>{user.email}</strong></p>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">Seu vínculo com o escritório</label>
                  <Select value={claim.position} onValueChange={v => setClaim({ ...claim, position: v })}>
                    <SelectTrigger data-testid="claim-position-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Sócio(a)">Sócio(a)</SelectItem>
                      <SelectItem value="Advogado(a) associado(a)">Advogado(a) associado(a)</SelectItem>
                      <SelectItem value="Administrativo/Marketing">Administrativo/Marketing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">Número OAB (se aplicável)</label>
                  <Input data-testid="claim-oab-input" placeholder="Ex: OAB/SP 123.456" value={claim.oab} onChange={e => setClaim({ ...claim, oab: e.target.value })} />
                </div>
                <label className="flex items-start gap-3 p-4 bg-[#f7fafb] rounded-[7px] cursor-pointer">
                  <Checkbox data-testid="claim-terms-checkbox" checked={claim.termsAccepted} onCheckedChange={v => setClaim({ ...claim, termsAccepted: !!v })} className="mt-0.5" />
                  <span className="text-sm text-[#354c54]">Declaro que as informações são verdadeiras e aceito os <strong>Termos de Uso e Política de Privacidade (v1.0-2026-06)</strong>. Estou ciente de que declarações falsas podem levar à suspensão do perfil.</span>
                </label>
                <Button onClick={submitClaim} disabled={claimBusy} className="w-full h-11 bg-[#153e44] hover:bg-[#0c3036] text-white font-semibold" data-testid="claim-submit-btn">
                  {claimBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><ShieldCheck className="w-4 h-4 mr-2" /> Reivindicar este perfil gratuitamente</>)}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
